import { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";
import { useMaterialUIController, setActivePage } from "context";

import DashboardHeader from "./components/DashboardHeader";
import KPICards from "./components/KPICards";
import OverallHealthPie from "./components/OverallHealthPie";
import GroupwiseHealth from "./components/GroupwiseHealth";
import AlertsSummary from "./components/AlertsSummary";
import DeviceStatusTable from "./components/DeviceStatusTable";
import PerformanceIndicators from "./components/PerformanceIndicators";
import AlertDrillDown from "./components/AlertDrillDown";
import InformationFlow from "./components/InformationFlow";
import AlertsDialog from "./components/dialogs/AlertsDialog";
import DeviceDetailDialog from "./components/dialogs/DeviceDetailDialog";
import useDashboardData from "./components/useDashboardData";
import { useICCCColors } from "./components/theme";
import { DEFAULT_POLL_MS } from "./model/constants";

export default function DeviceHealthDashboard({ onLoad }) {
  const [, dispatch] = useMaterialUIController();
  const ICCC_COLORS = useICCCColors();
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [timeRange, setTimeRange] = useState("all");
  const [alertsDialog, setAlertsDialog] = useState(null);
  const [detailDevice, setDetailDevice] = useState(null);
  const {
    rollup, perGroup, alertsSummary, bySeverityGroup, activeAlerts,
    isLoading, isFetching, refetch, lastUpdated,
  } = useDashboardData({ pollMs: DEFAULT_POLL_MS, timeRange });



  return (
    <MDBox
      sx={{
        px: 1, py: 1,
        bgcolor: ICCC_COLORS.pageBg,
        color: ICCC_COLORS.textPrimary,
        borderRadius: 1.5,
        minHeight: "calc(100vh - 120px)",
      }}
    >
      <DashboardHeader
        lastUpdated={lastUpdated}
        timeRange={timeRange}
        onTimeRangeChange={setTimeRange}
        onRefresh={refetch}
        isFetching={isFetching}
      />

      <MDBox mt={2}>
        <KPICards rollup={rollup} loading={isLoading} />
      </MDBox>

      <Grid container spacing={2} sx={{ mt: 0 }}>
        <Grid item xs={12} md={4}>
          <OverallHealthPie rollup={rollup} />
        </Grid>
        <Grid item xs={12} md={5}>
          <GroupwiseHealth
            groups={perGroup}
            selectedGroup={selectedGroup}
            onSelectGroup={(g) => setSelectedGroup(g.code)}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <AlertsSummary
            summary={alertsSummary}
            bySeverityGroup={bySeverityGroup}
            timeRange={timeRange}
            onViewAll={() =>
              setAlertsDialog({
                title: "All Active Alerts",
                alerts: activeAlerts,
              })
            }
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mt: 0 }}>
        <Grid item xs={12} md={6}>
          <DeviceStatusTable
            groupFilter={selectedGroup}
            groups={perGroup}
            onClearGroupFilter={() => setSelectedGroup(null)}
            onRowClick={setDetailDevice}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <PerformanceIndicators rollup={rollup} activeAlerts={activeAlerts} />
        </Grid>
        <Grid item xs={12} md={3}>
          <AlertDrillDown
            selectedGroup={selectedGroup}
            activeAlerts={activeAlerts}
            onClear={() => setSelectedGroup(null)}
          />
        </Grid>
      </Grid>

      <MDBox mt={2}>
        <InformationFlow rollup={rollup} />
      </MDBox>

      <AlertsDialog
        open={!!alertsDialog}
        config={alertsDialog}
        onClose={() => setAlertsDialog(null)}
        onSelectDevice={(device) => {
          setAlertsDialog(null);
          setDetailDevice(device);
        }}
      />

      <DeviceDetailDialog
        open={!!detailDevice}
        device={detailDevice}
        onClose={() => setDetailDevice(null)}
      />
    </MDBox>
  );
}
